import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import cx from 'classnames'
import Autosuggest from 'react-autosuggest'
import PropTypes from 'prop-types'
import debounce from 'lodash/debounce'
import take from 'lodash/take'
import Button from './Button'
import axios from 'axios'
import Input from './Input'

const COUNTRY = process.env.GATSBY_COUNTRY
const apiKey = process.env.GATSBY_HERE_API_KEY

const COUNTRIES_TO_HERE = {
  FI: 'FIN',
  SE: 'SWE',
  NO: 'NOR',
}

const DEFAULT_LANGUAGES = {
  FI: 'fi',
  SE: 'sv',
  NO: 'nb',
}

const ValuationInput = ({
  inputPlaceholder,
  containerClassName,
  className,
  buttonLabel,
  buttonClassName,
  buttonStyle,
  small,
}) => {
  const { t } = useTranslation()
  const [enteredAddress, setEnteredAddress] = useState('')
  const [location, setLocation] = useState({
    location_street_address: undefined,
    location_postcode: undefined,
    location_city: undefined,
  })
  const [locationError, setLocationError] = useState('')
  const [suggestions, setSuggestions] = useState([])

  const submitValuation = async (e) => {
    e.preventDefault()

    if (!location.location_street_address) {
      return setLocationError(
        t('The address could not be found, please try again.')
      )
    }

    const search = typeof window !== 'undefined' ? window.location.search : ''

    const url = new URL(
      `${
        process.env.GATSBY_BLOK_APP_URL
      }/valuator/${COUNTRY.toLowerCase()}${search}`
    )

    Object.keys(location).forEach((key) => {
      if (location[key]) {
        return url.searchParams.append(key, location[key])
      }
    })

    window.location.href = url
  }

  const getSuggestions = useCallback(
    debounce(async (value) => {
      const inputValue = value.trim().toLowerCase()
      const inputLength = inputValue.length

      if (inputLength < 3) {
        setLocation({
          location_street_address: undefined,
          location_postcode: undefined,
          location_city: undefined,
        })
        setSuggestions([])
        return
      }

      const { data: suggestionsData } = await axios.get(
        'https://autocomplete.geocoder.ls.hereapi.com/6.2/suggest.json',
        {
          params: {
            query: inputValue,
            apiKey,
            country: COUNTRIES_TO_HERE[COUNTRY],
            language: DEFAULT_LANGUAGES[COUNTRY],
            maxresults: 20,
          },
        }
      )

      if (suggestionsData.suggestions.length === 0) return setSuggestions([])

      const suggestionsArr = []

      suggestionsData.suggestions.forEach((item) => {
        if (item.matchLevel === 'street' || item.matchLevel === 'houseNumber') {
          const streetAddress = item.address?.houseNumber
            ? `${item.address?.street} ${item.address?.houseNumber}`
            : item.address?.street

          if (
            !suggestionsArr.some(
              (location) =>
                location.location_street_address === streetAddress &&
                location.location_city === item.address?.city
            )
          ) {
            suggestionsArr.push({
              location_street_address: streetAddress,
              location_postcode: item.address?.postalCode,
              location_city: item.address?.city,
            })
          }
        }
      })

      setSuggestions(take(suggestionsArr, 5))
    }, 250),
    []
  )

  const getSuggestionValue = (suggestion) =>
    `${suggestion.location_street_address}, ${suggestion.location_city}`

  const renderSuggestion = (suggestion) => {
    return (
      <div className="px-4 py-3 cursor-pointer">
        {suggestion.location_street_address}
        {suggestion.location_house_number &&
          ` ${suggestion.location_house_number}`}
        {suggestion.location_city && `, ${suggestion.location_city}`}
      </div>
    )
  }

  const handleChange = (event, { newValue }) => {
    if (event.type === 'change') {
      getSuggestions(newValue)
      setLocationError('')
    }
    setEnteredAddress(newValue)
  }

  const onSuggestionsFetchRequested = ({ value }) => {
    getSuggestions(value)
  }

  const onSuggestionsClearRequested = () => {
    setSuggestions([])
  }

  const onSuggestionSelected = (suggestion) => {
    setEnteredAddress(
      `${suggestion.location_street_address}, ${suggestion.location_city}`
    )
    setLocation({
      location_street_address: suggestion.location_street_address,
      location_postcode: suggestion.location_postcode,
      location_city: suggestion.location_city,
    })
  }

  return (
    <form className={containerClassName} onSubmit={(e) => submitValuation(e)}>
      <div className={className}>
        <Autosuggest
          suggestions={suggestions}
          onSuggestionsFetchRequested={onSuggestionsFetchRequested}
          onSuggestionsClearRequested={onSuggestionsClearRequested}
          getSuggestionValue={getSuggestionValue}
          renderSuggestion={renderSuggestion}
          onSuggestionSelected={(event, { suggestion }) => {
            onSuggestionSelected(suggestion)
          }}
          inputProps={{
            placeholder: inputPlaceholder ?? t('Enter your address'),
            value: enteredAddress,
            onChange: handleChange,
          }}
          renderInputComponent={(inputProps) => (
            <Input
              {...inputProps}
              size={small ? 'sm' : null}
              className={cx({ 'text-sm': small })}
            />
          )}
        />
        <Button
          tag="button"
          type="submit"
          size={small ? 'sm' : null}
          buttonLabel={buttonLabel ? buttonLabel : t('Valuate')}
          buttonStyle={buttonStyle}
          className={buttonClassName}
        />
      </div>
      {locationError ? (
        <span
          className={cx(
            'block mt-4 text-center text-danger',
            small ? 'text-sm' : 'text-base'
          )}
        >
          {locationError}
        </span>
      ) : null}
    </form>
  )
}

ValuationInput.propTypes = {
  inputPlaceholder: PropTypes.string,
  buttonLabel: PropTypes.string,
  className: PropTypes.string,
  buttonClassName: PropTypes.string,
  buttonStyle: PropTypes.string,
  small: PropTypes.bool,
}

export default ValuationInput
